import { Link } from 'react-router-dom';
import { ArrowLeft, Calculator, Check, ExternalLink, Eye, FileDown, MoreHorizontal, Pencil, Redo2, RotateCcw, Sparkles, Trash2, Undo2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import type { DocumentV3 } from '@docgen/schema';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { NativeSelect } from '../ui/select';
import { cn } from '../lib/utils';
import type { Zoom } from './Desk';
import type { ReadinessIssue } from '../lib/api';

export interface TopBarProps {
  doc: DocumentV3 | null;
  editMode: boolean;
  onEditMode: (on: boolean) => void;
  onRename: (name: string) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  saveState: 'saved' | 'saving' | 'dirty' | 'error';
  onSave: () => void;
  pageCount: number;
  warnings: string[];
  zoom: Zoom;
  onZoom: (zoom: Zoom) => void;
  /** The proposal's print view URL (browser Save-as-PDF). */
  exportHref?: string;
  /** Exports without opening the print view (the Salesforce embed, whose session a new tab cannot share). Wins over `exportHref`. */
  onExport?: () => void;
  /** What stops the export; when any, the PDF button reports them instead of opening the print view. */
  exportIssues?: readonly ReadinessIssue[];
  /** Called when the PDF button is pressed while `exportIssues` is non-empty. */
  onExportBlocked?: () => void;
  /** Whether the Pricing panel is showing in place of the inspector. */
  pricingOpen: boolean;
  onPricingOpen: (on: boolean) => void;
  /** Asks to reset the proposal to the template (the caller confirms first). */
  onReset?: () => void;
  /** Asks to delete the proposal (the caller confirms first). */
  onDelete?: () => void;
  /** The Salesforce Opportunity this proposal was built for; shows "Back to opportunity". */
  opportunityHref?: string;
  /** Where the back arrow goes; no arrow without it (the Salesforce embed has no app to go back to). */
  backHref?: string;
  /** Shows a primary Done button at the far right (the Salesforce embed closes its window on it). */
  onDone?: () => void;
  /** An Assistant toggle beside Done (the Salesforce embed, which has no app shell to hold one). */
  assistant?: EditorAssistantToggle;
}

/** The embed's assistant panel: whether it is open, and the toggle. */
export interface EditorAssistantToggle {
  readonly open: boolean;
  readonly onToggle: () => void;
}

const SAVE_LABEL: Record<TopBarProps['saveState'], string> = {
  saved: 'Saved',
  saving: 'Saving…',
  dirty: 'Unsaved changes',
  error: 'Save failed',
};

const ZOOM_OPTIONS: { value: string; label: string }[] = [
  { value: 'fit', label: 'Fit' },
  { value: '0.75', label: '75%' },
  { value: '1', label: '100%' },
  { value: '1.25', label: '125%' },
];

export function TopBar(props: TopBarProps) {
  const { doc, editMode, onEditMode, onRename, undo, redo, canUndo, canRedo, saveState, onSave, pageCount, warnings, zoom, onZoom, exportHref, onExport, exportIssues = [], onExportBlocked, pricingOpen, onPricingOpen, onReset, onDelete, opportunityHref, backHref, onDone, assistant } = props;
  const exportBlocked = exportIssues.length > 0;
  return (
    <header className="flex h-12 shrink-0 items-center gap-1.5 border-b bg-card px-2.5">
      {backHref ? (
        <Button variant="ghost" size="icon" aria-label="Back to documents" asChild>
          <Link to={backHref}>
            <ArrowLeft />
          </Link>
        </Button>
      ) : null}
      {doc && (
        <Input
          value={doc.name}
          aria-label="Document name"
          className="w-72 border-transparent text-[13px] font-semibold shadow-none hover:border-input focus-visible:border-input"
          onChange={(e) => onRename(e.target.value)}
        />
      )}
      {opportunityHref ? (
        <Button variant="ghost" size="sm" asChild>
          <a href={opportunityHref}>
            <ExternalLink /> Back to opportunity
          </a>
        </Button>
      ) : null}
      <div className="flex items-center">
        <Button variant="ghost" size="icon" aria-label="Undo" disabled={!canUndo} onClick={undo}>
          <Undo2 />
        </Button>
        <Button variant="ghost" size="icon" aria-label="Redo" disabled={!canRedo} onClick={redo}>
          <Redo2 />
        </Button>
      </div>
      <span
        className={cn('ml-1 text-[11px] text-muted-foreground', saveState === 'error' && 'text-destructive')}
      >
        {SAVE_LABEL[saveState]}
      </span>
      <div className="ml-auto flex items-center gap-2">
        <span
          className={cn('text-[11px] text-muted-foreground', warnings.length > 0 && 'text-destructive')}
          title={warnings.join('\n')}
        >
          {warnings.length > 0 ? `${warnings.length} ⚠ · ` : ''}
          {pageCount} pages
        </span>
        <NativeSelect
          aria-label="Zoom"
          className="h-7 w-20 text-xs"
          value={typeof zoom === 'number' ? String(zoom) : 'fit'}
          onChange={(e) => onZoom(e.target.value === 'fit' ? 'fit' : Number(e.target.value))}
        >
          {ZOOM_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </NativeSelect>
        <Button
          variant={pricingOpen ? 'default' : 'outline'}
          size="sm"
          aria-pressed={pricingOpen}
          onClick={() => onPricingOpen(!pricingOpen)}
        >
          <Calculator /> Pricing
        </Button>
        <Button variant="secondary" size="sm" onClick={onSave} disabled={saveState === 'saving'}>
          Save
        </Button>
        <Button
          variant="secondary"
          size="sm"
          title={exportBlocked ? 'Fix the issues listed before exporting' : 'Export PDF'}
          disabled={!exportHref && !onExport}
          onClick={() => {
            if (exportBlocked) onExportBlocked?.();
            else if (onExport) onExport();
            else if (exportHref) window.open(exportHref, '_blank', 'noopener');
          }}
        >
          <FileDown /> PDF
        </Button>
        <Button variant={editMode ? 'outline' : 'default'} size="sm" onClick={() => onEditMode(!editMode)}>
          {editMode ? <Eye /> : <Pencil />}
          {editMode ? 'Preview' : 'Edit'}
        </Button>
        {onReset || onDelete ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="More proposal actions">
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onReset ? (
                <DropdownMenuItem onSelect={onReset}>
                  <RotateCcw /> Reset to template
                </DropdownMenuItem>
              ) : null}
              {onReset && onDelete ? <DropdownMenuSeparator /> : null}
              {onDelete ? (
                <DropdownMenuItem onSelect={onDelete} className="text-destructive focus:text-destructive">
                  <Trash2 /> Delete proposal
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
        {assistant ? (
          <Button size="sm" variant={assistant.open ? 'secondary' : 'outline'} aria-pressed={assistant.open} onClick={assistant.onToggle}>
            <Sparkles /> Assistant
          </Button>
        ) : null}
        {onDone ? (
          <Button size="sm" onClick={onDone} disabled={saveState === 'saving'}>
            <Check /> Done
          </Button>
        ) : null}
      </div>
    </header>
  );
}
